import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Grid } from '@mui/material';
import { Bar, LinePath } from '@visx/shape';
import { useTooltip, TooltipWithBounds } from '@visx/tooltip';
import { AxisBottom } from '@visx/axis';
import { scaleLinear } from '@visx/scale';
import Truncate from 'react-truncate';
import PropTypes from 'prop-types';

import { getParams, thousandFormat2, printFormat } from '../../../../Utils/Functions';
import {
  positiveActiveColor,
  positiveDisableColor,
  negativeActiveColor,
  negativeDisableColor,
  barThinHeight,
  formatedStyle,
  enMonths,
} from '../../../../Assets/js/constant';

let tooltipTimeout;

const GROUP_ORDER = [
  'Cash flow from Operating Activities',
  'Cash flow from Investing Activities',
  'Cash flow from Financing Activities',
];

// normalise common typos/variants to the canonical labels above
function normaliseGroupName(name) {
  if (!name) return name;
  const s = String(name).trim().toLowerCase();
  if (s.includes('operating')) return GROUP_ORDER[0];
  if (s.includes('invest')) return GROUP_ORDER[1];
  if (s.includes('financ')) return GROUP_ORDER[2];
  return name; // fallback as-is
}

const MiddleChart = ({
  middleData,
  selectedTopItems,
  selectedMiddleItems,
  dimDate,
  defaultDimDate,
  defaultStartMonth,
  maxCashFlowGroupName,
  maxCashFlowCategoryName,
  updateFilter,
}) => {
  const { tooltipOpen, tooltipTop, tooltipLeft, tooltipData, showTooltip, hideTooltip } = useTooltip();

  const [resize, setResize] = useState(false);
  const [hoverBar, setHoverBar] = useState(null);
  const [selectedBars, setSelectedBars] = useState([]);
  const [selectedBarObjs, setSelectedBarObjs] = useState([]);
  const [data, setData] = useState([]);
  const [group, setGroup] = useState([]);

  const handleResize = useCallback(() => {
    setResize(prev => !prev);
  }, []);

  useEffect(() => {
    window.addEventListener('resize', handleResize);
    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, [handleResize]);

  const prepareData = useCallback(() => {
    // Normalize to "YYYY-MM"
    const toYM = (d) => {
      if (!d) return null;
      if (typeof d === 'string') {
        if (/^\d{4}-\d{2}-\d{2}/.test(d)) return d.slice(0, 7);           // "YYYY-MM-DD"
        if (/^\d{8}$/.test(d)) return `${d.slice(0, 4)}-${d.slice(4, 6)}`; // "YYYYMMDD"
        const t = new Date(d);
        return isNaN(t) ? null : t.toISOString().slice(0, 7);
      }
      if (d instanceof Date && !isNaN(d)) return d.toISOString().slice(0, 7);
      if (typeof d === 'number') {
        const s = String(d);
        if (s.length >= 6) return `${s.slice(0, 4)}-${s.slice(4, 6)}`;
      }
      return null;
    };

    // Selected months from dimDate values like "YYYYMMDD".
    // Multi-select should aggregate all selected months, not collapse back to the first month.
    const selectedYMs = Array.isArray(dimDate)
      ? dimDate
          .map((rawDim) =>
            rawDim
              ? `${String(rawDim).slice(0, 4)}-${String(rawDim).slice(4, 6)}`
              : null,
          )
          .filter(Boolean)
      : [];

    // Start with API data
    let rows = Array.isArray(middleData) ? middleData : [];

    // 1) Prefer filtering by the selected month set if provided and yields rows
    let filtered = rows;
    if (selectedYMs.length) {
      const bySelectedMonths = rows.filter((r) => selectedYMs.includes(toYM(r?.Date)));
      if (bySelectedMonths.length) filtered = bySelectedMonths;
    }

    // 2) If no selected month is available and multiple months are returned,
    // lock to a single month deterministically for the default view only.
    if (!selectedYMs.length && filtered.length) {
      const ymMap = new Map();
      const ymNonZero = new Map();
      filtered.forEach((r) => {
        const ym = toYM(r?.Date);
        if (!ym) return;
        if (!ymMap.has(ym)) ymMap.set(ym, []);
        ymMap.get(ym).push(r);
        const actual = Number(r?.CashChange) || 0;
        if (actual !== 0) {
          if (!ymNonZero.has(ym)) ymNonZero.set(ym, []);
          ymNonZero.get(ym).push(r);
        }
      });

      const asc = (a, b) => a.localeCompare(b);
      const allMonths = Array.from(ymMap.keys()).sort(asc);
      const nzMonths = Array.from(ymNonZero.keys()).sort(asc);

      const now = new Date();
      const nowYM = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;

      const leToday = (arr) => arr.filter(m => m <= nowYM);
      const pickLast = (arr) => (arr.length ? arr[arr.length - 1] : null);

      const chosenYM =
        pickLast(leToday(nzMonths)) ??
        pickLast(leToday(allMonths)) ??
        pickLast(allMonths);

      if (chosenYM) {
        filtered = filtered.filter((r) => toYM(r?.Date) === chosenYM);
      }
    }

    // 3) Aggregate by (CashFlowGroup + CashFlowCategory), using Actual when non-zero else Forecast
    const dataObj = Object.create(null);
    const groupsPresent = new Set();

    filtered.forEach((item) => {
      if (!item || !item.CashFlowGroup || !item.CashFlowCategory) return;
      const actual = Number(item.CashChange) || 0;
      const forecast = Number(item.ForecastCashChange) || 0;
      const hasProjectedValue = item.ProjectedCashFlowValue !== undefined && item.ProjectedCashFlowValue !== null;
      const value = hasProjectedValue ? Number(item.ProjectedCashFlowValue) || 0 : (actual !== 0 ? actual : forecast);
      if (value === 0) return;

      const groupName = normaliseGroupName(item.CashFlowGroup);
      const key = `${groupName}||${item.CashFlowCategory}`;
      if (!dataObj[key]) {
        dataObj[key] = {
          CashFlowGroup: groupName,
          CashFlowCategory: item.CashFlowCategory,
          ForecastCashChange: 0, // renderer expects this prop name
        };
      }
      dataObj[key].ForecastCashChange += value;
      groupsPresent.add(groupName);
    });

    // turn into array
    const _data = Object.values(dataObj);

    // 4) Order within each group: positives first (DESC), then negatives (ASC i.e., most negative first)
    const sortWithinGroup = (a, b) => {
      const av = a.ForecastCashChange || 0;
      const bv = b.ForecastCashChange || 0;
      const apos = av >= 0;
      const bpos = bv >= 0;
      if (apos !== bpos) return apos ? -1 : 1;           // positives first
      if (apos) return bv - av;                          // both positive: larger first
      return av - bv;                                    // both negative: more negative first
    };

    // We'll keep data unsorted globally, but when rendering or computing domain,
    // we will apply group ordering + sortWithinGroup
    setData(_data);

    // 5) Order groups by our canonical order; include only those present
    const orderedGroups = GROUP_ORDER.filter(g => groupsPresent.has(g));
    setGroup(orderedGroups);
  }, [middleData, dimDate]);

  useEffect(() => {
    prepareData();
  }, [prepareData, selectedMiddleItems]);

  const getColor = useCallback((bar) => {
    if (bar.ForecastCashChange === undefined) return positiveActiveColor;

    const activeColor  = bar.ForecastCashChange > 0 ? positiveActiveColor : negativeActiveColor;
    const disableColor = bar.ForecastCashChange > 0 ? positiveDisableColor : negativeDisableColor;

    if (!selectedBarObjs || selectedBarObjs.length === 0) return activeColor;

    const isSelected = selectedBarObjs.some(
      (it) =>
        it?.CashFlowGroup === bar.CashFlowGroup &&
        it?.CashFlowCategory === bar.CashFlowCategory
    );
    return isSelected ? activeColor : disableColor;
  }, [selectedBarObjs]);

  const handleBar = useCallback((event, bar) => {
    let _selectedBars, _selectedBarObjs, _selectedMiddleItems;

    if (event.shiftKey) {
      _selectedBars = selectedBars.slice();
      _selectedBarObjs = selectedBarObjs.slice();
      _selectedMiddleItems = selectedMiddleItems.slice();

      let index = NaN;
      for (let i = 0; i < selectedBars.length; i++) {
        if (selectedBars[i] === event.target) {
          index = i;
          break;
        }
      }

      if (isNaN(index)) {
        event.target.classList.add('barActive');
        _selectedBars.push(event.target);
        _selectedBarObjs.push(bar);
        _selectedMiddleItems.push({
          CashFlowCategory: bar.CashFlowCategory,
          CashFlowGroup: bar.CashFlowGroup,
        });
      } else {
        event.target.classList.remove('barActive');
        _selectedBars.splice(index, 1);
        _selectedBarObjs.splice(index, 1);
        _selectedMiddleItems.splice(index, 1);
      }
    } else {
      let exist = false;

      selectedBars.forEach((selectedBar) => {
        selectedBar.classList.remove('barActive');
        if (selectedBar === event.target) exist = true;
      });

      if (exist && selectedBars.length === 1) {
        _selectedBars = [];
        _selectedBarObjs = [];
        _selectedMiddleItems = [];
      } else {
        _selectedMiddleItems = [
          {
            CashFlowCategory: bar.CashFlowCategory,
            CashFlowGroup: bar.CashFlowGroup,
          },
        ];
        _selectedBars = [event.target];
        _selectedBarObjs = [bar];
        event.target.classList.add('barActive');
      }
    }

    setSelectedBars(_selectedBars);
    setSelectedBarObjs(_selectedBarObjs);

    updateFilter({
      selectedMiddleItems: _selectedMiddleItems,
    });
  }, [selectedBars, selectedBarObjs, selectedMiddleItems, updateFilter]);

  const deSelectAll = useCallback(() => {
    if (hoverBar) hoverBar.classList.remove('barHover');

    selectedBars.forEach((selectedBar) => {
      selectedBar.classList.remove('barActive');
    });
    setHoverBar(null);
    setSelectedBars([]);
    setSelectedBarObjs([]);
  }, [hoverBar, selectedBars]);

  const showTooltipHandler = useCallback((event, xScale, bar, isBar = true) => {
    if (tooltipTimeout) clearTimeout(tooltipTimeout);
    let data = {},
      top,
      left;

    top = event.clientY - 60;
    left =
      window.innerWidth / 2 < event.nativeEvent.layerX
        ? event.nativeEvent.layerX - 350
        : event.nativeEvent.layerX + 50;

    if (isBar) {
      if (hoverBar) hoverBar.classList.remove('barHover');
      event.target.classList.add('barHover');
      setHoverBar(event.target);
      data = bar;
    } else {
      data['forecast'] = bar;
    }

    data['isBar'] = isBar;

    showTooltip({
      tooltipData: data,
      tooltipTop: top,
      tooltipLeft: left,
    });
  }, [hoverBar, showTooltip]);

  const hideTooltipHandler = useCallback(() => {
    tooltipTimeout = setTimeout(() => {
      if (hoverBar) hoverBar.classList.remove('barHover');
      setHoverBar(null);
      hideTooltip();
    }, 300);
  }, [hoverBar, hideTooltip]);

  const dimDates = useMemo(() => {
    let _dimDate = [];
    if (selectedTopItems.length === 0) {
      _dimDate.push(Math.floor(defaultDimDate % 10000 / 100));
    } else {
      selectedTopItems.forEach((item) => {
        let _DimDate = printFormat.dateYYYYMMDD(
          item.month + 1 >= defaultStartMonth
            ? item.year - 1
            : item.year,
          item.month + 1,
        );
        _dimDate.push(Math.floor(_DimDate % 10000 / 100));
      });
    }
    return _dimDate.sort((a, b) => a - b);
  }, [selectedTopItems, defaultStartMonth, defaultDimDate]);

  const width = window.innerWidth - 5;
  const height = 300;
  const margin = {
    top: 0,
    right: 0,
    bottom: 20,
    left: 10,
  };

  const { xMax } = getParams(window, width, height, margin);

  // ---- Build a rendering plan with the exact order we want (groups + rows sorted by sign)
  const rowsByGroup = useMemo(() => {
    // sorter defined here to share across domain + render
    const sortWithinGroup = (a, b) => {
      const av = a.ForecastCashChange || 0;
      const bv = b.ForecastCashChange || 0;
      const apos = av >= 0;
      const bpos = bv >= 0;
      if (apos !== bpos) return apos ? -1 : 1; // positives first
      if (apos) return bv - av;                // pos: larger first
      return av - bv;                          // neg: more negative first
    };

    const map = new Map();
    group.forEach(g => map.set(g, []));
    data.forEach(d => {
      const g = d.CashFlowGroup;
      if (map.has(g)) map.get(g).push(d);
    });
    group.forEach(g => {
      map.set(g, (map.get(g) || []).slice().sort(sortWithinGroup));
    });
    return map;
  }, [data, group]);

  // ---- DOMAIN using the same ordered plan
  let domainMin = 0, domainMax = 0;
  {
    let running = 0;
    group.forEach((g) => {
      const rows = rowsByGroup.get(g) || [];
      rows.forEach((item) => {
        running += item.ForecastCashChange;
        domainMin = Math.min(domainMin, running, item.ForecastCashChange);
        domainMax = Math.max(domainMax, running, item.ForecastCashChange);
      });
    });
    if (domainMin > 0) domainMin = 0;
  }

  const axisWidth =
    window.innerWidth -
    (maxCashFlowGroupName + maxCashFlowCategoryName) * 6.2 -
    33;

  const xScale = scaleLinear({
    range: [0, axisWidth],
    domain: [domainMin, domainMax],
    nice: true,
  });

  let heightPercent = 30;

  if (document.getElementById('projectedcashflow-middle-root')) {
    let middleHeight = 20 * data.length + 60;
    if (Math.ceil((middleHeight / window.innerHeight) * 100) + 1 > 30) {
      if (Math.ceil((middleHeight / window.innerHeight) * 100) + 1 > 50)
        heightPercent = 50;
      else
        heightPercent =
          Math.ceil((middleHeight / window.innerHeight) * 100) + 1;
    }
  }

  // Continuous waterfall across sections
  let cumulativeStart = 0;

  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        height: `${heightPercent}vh`,
      }}
      id="projectedcashflow-middle-root"
    >
      <div>
        <div className="subtitle mt-10 mb-5">
          Cash Flow{' '}
          {dimDates.map((item) => enMonths[item - 1]).join(', ')}{' '}
          Actual
        </div>
        <div style={formatedStyle(xMax, 12, 0, 0, 'pointer')} id="wrapper">
          <Grid
            container
            style={{
              overflow: 'hidden auto',
              maxHeight: `${heightPercent}vh`,
            }}
          >
            {group.map((grupItem, i) => {
              // start each section where the previous section finished
              let running = cumulativeStart;
              const yMax = barThinHeight - 5;

              const rowsInGroup = rowsByGroup.get(grupItem) || [];
              if (rowsInGroup.length === 0) return null;

              const rowEl = (
                <Grid
                  container
                  key={i}
                  style={{
                    borderTop: 'solid 1px #a9a9a9',
                  }}
                >
                  <div
                    style={{
                      width: maxCashFlowGroupName * 6.2,
                    }}
                  >
                    <p
                      style={{
                        paddingLeft: 2,
                        margin: '2.5px 0 0',
                        whiteSpace: 'nowrap',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                      }}
                    >
                      <Truncate ellipsis={<span>...</span>}>
                        {grupItem}
                      </Truncate>
                    </p>
                  </div>

                  <div
                    style={{
                      width: window.innerWidth - maxCashFlowGroupName * 6.2 - 20,
                    }}
                  >
                    {rowsInGroup.map((item, j) => {
                      const v = item.ForecastCashChange || 0;
                      const next = running + v;

                      const x1 = xScale(running);
                      const x2 = xScale(next);
                      const barX = Math.min(x1, x2);
                      const barWidth = Math.abs(x2 - x1);
                      const barY = 1;
                      const barHeight = 18;

                      running = next;

                      return (
                        <Grid container key={j}>
                          <div
                            style={{
                              width: maxCashFlowCategoryName * 6.2,
                              display: 'flex',
                              alignItems: 'center',
                            }}
                          >
                            <p
                              style={{
                                paddingLeft: 2,
                                margin: 0,
                                width: '100%',
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                whiteSpace: 'nowrap',
                              }}
                            >
                              <Truncate ellipsis={<span>...</span>}>
                                {item.CashFlowCategory}
                              </Truncate>
                            </p>
                          </div>
                          <div
                            style={{
                              width:
                                window.innerWidth -
                                (maxCashFlowGroupName + maxCashFlowCategoryName) * 6.2 -
                                20,
                              display: 'flex',
                              alignItems: 'center',
                            }}
                          >
                            <svg height={yMax} width={axisWidth}>
                              <rect
                                x={0}
                                y={0}
                                height={yMax}
                                fill="transparent"
                                onClick={deSelectAll}
                              />
                              <Bar
                                x={barX}
                                y={barY}
                                width={barWidth}
                                height={barHeight}
                                fill={getColor(item)}
                                onClick={(event) => handleBar(event, item)}
                                onMouseLeave={hideTooltipHandler}
                                onMouseMove={(event) =>
                                  showTooltipHandler(event, xScale, item)
                                }
                                onTouchEnd={hideTooltipHandler}
                                onTouchMove={(event) =>
                                  showTooltipHandler(event, xScale, item)
                                }
                              />
                              <LinePath
                                data={[
                                  { x: xScale(0), y: 0 },
                                  { x: xScale(0), y: yMax },
                                ]}
                                x={(d) => (d.x ? d.x : 0)}
                                y={(d) => (d.y ? d.y : 0)}
                                stroke="#555555"
                                strokeDasharray="6,2"
                              />
                            </svg>
                          </div>
                        </Grid>
                      );
                    })}
                  </div>
                </Grid>
              );

              // carry the end of this section into the next section
              cumulativeStart = running;
              return rowEl;
            })}
          </Grid>

          {data.length > 0 ? (
            <div
              style={{ height: 28, borderTop: 'solid 1px #a9a9a9' }}
              onClick={deSelectAll}
            >
              <svg
                width={window.innerWidth - 15}
                height={barThinHeight}
                style={{ overflow: 'visible' }}
              >
                <AxisBottom
                  scale={xScale}
                  top={0}
                  left={(maxCashFlowGroupName + maxCashFlowCategoryName) * 6.2}
                  hideAxisLine={true}
                  stroke="black"
                  numTicks={10}
                  tickStroke="#a9a9a9"
                  tickFormat={(value) => value / 1000}
                  tickLabelProps={() => ({
                    fill: 'black',
                    fontSize: 11,
                    textAnchor: 'middle',
                    dy: '0.2em',
                  })}
                  tickComponent={({ formattedValue, ...tickProps }) => (
                    <text {...tickProps}>
                      {formattedValue >= 0
                        ? Math.abs(formattedValue) < 1000
                          ? `$${formattedValue}K`
                          : `$${formattedValue / 1000}M`
                        : Math.abs(formattedValue) < 1000
                        ? `-$${Math.abs(formattedValue)}K`
                        : `-$${Math.abs(formattedValue) / 1000}M`}
                    </text>
                  )}
                />
              </svg>
            </div>
          ) : null}
        </div>
      </div>

      {tooltipOpen && (
        <TooltipWithBounds left={tooltipLeft} top={tooltipTop}>
          {tooltipData.isBar ? (
            <div
              className="bkg-white color-black"
              style={{ width: tooltipData.CashFlowGroup.length * 8.5 }}
            >
              <div className="pdv-10">
                <strong>
                  {tooltipData.CashFlowGroup}:&nbsp;
                  {tooltipData.CashFlowCategory}
                </strong>
              </div>
              <div className="flex">
                <strong>{' '}{thousandFormat2(tooltipData.ForecastCashChange)}</strong>
              </div>
            </div>
          ) : null}
        </TooltipWithBounds>
      )}
    </div>
  );
};

MiddleChart.propTypes = {
  middleData: PropTypes.array.isRequired,
  selectedTopItems: PropTypes.array.isRequired,
  selectedMiddleItems: PropTypes.array.isRequired,
  dimDate: PropTypes.array.isRequired,
  defaultDimDate: PropTypes.number.isRequired,
  defaultStartMonth: PropTypes.number.isRequired,
  maxCashFlowGroupName: PropTypes.number.isRequired,
  maxCashFlowCategoryName: PropTypes.number.isRequired,
  updateFilter: PropTypes.func.isRequired,
};

export default MiddleChart;
